import { useEffect, type FC } from 'react';
import { type UiStackProps } from '@/lib/ui';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useMutation } from '@tanstack/react-query';
import { type CreatePageVisitRequest, type PageVisitResponse } from '@/api/registration';
import { registration } from '@/api';
import { type ApiResponseSingle } from '@/api/tenantClient';
import { loadItem, saveItem } from '@/api/local';

export interface PageVisitTrackerProps extends UiStackProps {
  pageName: string
}

const LAST_TRACKING_PAGE_KEY = 'last_tracking_page';
interface CachePageVisitTracker {
  pageName: string
}

const PageVisitTracker: FC<PageVisitTrackerProps> = ({
  pageName,
  children,
}) => {
  const { eventId } = useRegisterRoute();
  const { createTenantApiRequest, isLoading: isApiPreparing } = useTenantApi();
  const registrationQuery = useRegistrationQuery(eventId);

  const { mutate: createPageVisitMutate } = useMutation<ApiResponseSingle<PageVisitResponse>, Error, CreatePageVisitRequest>(
    {
      mutationFn: async (data: CreatePageVisitRequest) => {
        return await registration.createPageVisit(createTenantApiRequest)(data);
      },
      onSuccess: async (response) => {}
    }
  );

  useEffect(() => {
    if (!isApiPreparing) {
      const savedPageItem = loadItem<CachePageVisitTracker>({ key: LAST_TRACKING_PAGE_KEY });
      const registrationId = registrationQuery?.data?.id;
      if (registrationId && savedPageItem?.item?.pageName !== pageName) {
        createPageVisitMutate({ registrationId, pageName });
        saveItem({ key: LAST_TRACKING_PAGE_KEY, item: JSON.stringify({ pageName }) });
      }
    }
  }, [
    createTenantApiRequest,
    createPageVisitMutate,
    isApiPreparing,
    pageName,
    registrationQuery?.data?.id,
  ]);

  return <>{children}</>;
};

export default PageVisitTracker;
